import React, { useState, useEffect, useRef } from 'react';
import Tooltip from '../ui/Tooltip';

function CropFrame({
  x,
  y,
  width,
  height,
  initialWidth,
  initialHeight,
  onMove,
  onResize,
  zIndex,
  selected,
  onSelect,
  onRemove,
  onConstrain,
  isLocked,
  onToggleLock,
  isLargerThanImage,
  zoomFactor,
  onDoubleClick,
  drawMode, // new prop: prefer this over global window flag
  shiftHeld,
  isDrawingFrame,
  recentlyDrawn,
}) {
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  // Refs to hold initial mouse/frame state for smooth dragging/resizing without
  // causing re-renders or closure-staleness problems.
  const startMouseRef = useRef({ x: 0, y: 0 });
  const startFrameRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const resizeHandleRef = useRef(null);
  const [cursorType, setCursorType] = useState(undefined);
  // Local live frame state used during an active drag/resize so the element
  // updates immediately without waiting for parent state to propagate.
  const proportionalResizeRef = useRef(false);
  const [localFrame, setLocalFrame] = useState({ x, y, width, height });
  const localFrameRef = useRef(localFrame);
  const [grabbing, setGrabbing] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const [draggingStarted, setDraggingStarted] = useState(false);
  const justPersistedRef = useRef(false);
  const [editing, setEditing] = useState(false);
  const [editWidth, setEditWidth] = useState(Math.round(initialWidth || width));
  const [editHeight, setEditHeight] = useState(Math.round(initialHeight || height));
  const [labelHover, setLabelHover] = useState(false);

  const widthInputRef = useRef(null);
  const widthSizerRef = useRef(null);
  const heightInputRef = useRef(null);
  const heightSizerRef = useRef(null);
  const initialWidthMin = useRef(null);
  const initialHeightMin = useRef(null);
  // Dynamically adjust input width to fit contents
  // Set min width only once when edit mode is activated
  useEffect(() => {
    if (editing) {
      // Always reset min width on edit mode activation
      if (widthInputRef.current && widthSizerRef.current) {
        widthSizerRef.current.textContent = editWidth;
        initialWidthMin.current = widthSizerRef.current.offsetWidth + 16;
      }
      if (heightInputRef.current && heightSizerRef.current) {
        heightSizerRef.current.textContent = editHeight;
        initialHeightMin.current = heightSizerRef.current.offsetWidth + 16;
      }
    }
  }, [editing, editWidth, editHeight]);

  useEffect(() => {
    if (editing) widthInputRef.current?.focus();
  }, [editing]);

  // Only grow input width if value gets longer
  useEffect(() => {
    if (widthInputRef.current && widthSizerRef.current && initialWidthMin.current != null) {
      widthSizerRef.current.textContent = editWidth;
      // keep sizer content for measurement but do not modify input style to avoid jumps;
      // input width is controlled by JSX inline style (ch-based) and container minWidth.
    }
    if (heightInputRef.current && heightSizerRef.current && initialHeightMin.current != null) {
      heightSizerRef.current.textContent = editHeight;
    }
  }, [editWidth, editHeight]);

  const delay = 200; // Delay in milliseconds

  // Calculate the original aspect ratio based on original dimensions
  const baseW = initialWidth || width;
  const baseH = initialHeight || height;
  const originalAspectRatio = baseH ? baseW / baseH : 1;
  // Live display dims
  const isProportionalDisplay = (dragging || resizing) && proportionalResizeRef.current;
  const usingLocalForRender = dragging || resizing;
  // Label shows initial (baseline) dimensions if available, otherwise current size
  const displayWidth = Math.round(initialWidth || width);
  const displayHeight = Math.round(initialHeight || height);
  const displayAspectRatio = displayHeight ? (displayWidth / displayHeight) : originalAspectRatio;
  const dimsMinCh = Math.max(String(displayWidth).length, String(displayHeight).length, 1) + 2;

  // Keep localFrame in sync with incoming props when not interacting.
  useEffect(() => {
    localFrameRef.current = localFrame;
  }, [localFrame]);

  useEffect(() => {
    if (justPersistedRef.current) {
      // Clear the one-time guard and allow normal sync on next update
      justPersistedRef.current = false;
      return;
    }
    if (!dragging && !resizing) {
      if (
        localFrame.x !== x ||
        localFrame.y !== y ||
        localFrame.width !== width ||
        localFrame.height !== height
      ) {
        setLocalFrame({ x, y, width, height });
      }
    }
  }, [x, y, width, height, dragging, resizing, localFrame]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const deltaX = (e.clientX - startMouseRef.current.x) / zoomFactor;
      const deltaY = (e.clientY - startMouseRef.current.y) / zoomFactor;

    if (dragging) {
        if (!draggingStarted) {
          setDraggingStarted(true);
          setGrabbing(true);
        }

  // Use the frame position captured at mousedown to avoid accumulating
  // rounding errors and closure-stale values that cause jumps.
  const baseX = startFrameRef.current.x;
  const baseY = startFrameRef.current.y;
  const newX = baseX + deltaX;
  const newY = baseY + deltaY;

  // Update local frame immediately for snappy UI, and also notify
  // parent so external components see the live change.
  setLocalFrame(prev => ({ ...prev, x: newX, y: newY }));
  onMove(Math.round(newX), Math.round(newY));
      } else if (resizing) {
          const baseW = startFrameRef.current.width;
          const baseH = startFrameRef.current.height;
          const baseX = startFrameRef.current.x;
          const baseY = startFrameRef.current.y;
          let newWidth = baseW;
          let newHeight = baseH;
          let newX = baseX;
          let newY = baseY;
          let updateOriginal = false;

          const handle = resizeHandleRef.current || 'br';
          const sideHandles = ['t','r','b','l'];
          const isSideHandle = sideHandles.includes(handle);

          // Compute new dims according to which handle is active
          switch (handle) {
            case 'br':
              newWidth = baseW + deltaX;
              newHeight = baseH + deltaY;
              break;
            case 'bl':
              newWidth = baseW - deltaX;
              newHeight = baseH + deltaY;
              newX = baseX + deltaX;
              break;
            case 'tl':
              newWidth = baseW - deltaX;
              newHeight = baseH - deltaY;
              newX = baseX + deltaX;
              newY = baseY + deltaY;
              break;
            case 't':
              newHeight = baseH - deltaY;
              newY = baseY + deltaY;
              break;
            case 'b':
              newHeight = baseH + deltaY;
              break;
            case 'l':
              newWidth = baseW - deltaX;
              newX = baseX + deltaX;
              break;
            case 'r':
              newWidth = baseW + deltaX;
              break;
            // top-right intentionally omitted (user requested not resizable)
            default:
              newWidth = baseW + deltaX;
              newHeight = baseH + deltaY;
          }

          // Option (Alt) key: mirror scaling around center (anchor at frame center)
          if (e.altKey) {
            // Horizontal mirroring
            if (['l','r','tl','bl','br'].includes(handle)) {
              if (handle === 'r') {
                newWidth = baseW + 2 * deltaX;
                newX = baseX - deltaX;
              } else if (handle === 'l') {
                newWidth = baseW - 2 * deltaX;
                newX = baseX + deltaX;
              } else if (handle === 'br' || handle === 'bl' || handle === 'tl') {
                // For corners, apply both axes below
                // horizontal part
                if (handle === 'br') {
                  newWidth = baseW + 2 * deltaX;
                  newX = baseX - deltaX;
                } else if (handle === 'bl') {
                  newWidth = baseW - 2 * deltaX;
                  newX = baseX + deltaX;
                } else if (handle === 'tl') {
                  newWidth = baseW - 2 * deltaX;
                  newX = baseX + deltaX;
                }
              }
            }
            // Vertical mirroring
            if (['t','b','tl','tr','bl','br'].includes(handle)) {
              if (handle === 'b') {
                newHeight = baseH + 2 * deltaY;
                newY = baseY - deltaY;
              } else if (handle === 't') {
                newHeight = baseH - 2 * deltaY;
                newY = baseY + deltaY;
              } else if (handle === 'br' || handle === 'bl' || handle === 'tl' || handle === 'tr') {
                if (handle === 'br') {
                  newHeight = baseH + 2 * deltaY;
                  newY = baseY - deltaY;
                } else if (handle === 'bl') {
                  newHeight = baseH + 2 * deltaY;
                  newY = baseY - deltaY;
                } else if (handle === 'tl') {
                  newHeight = baseH - 2 * deltaY;
                  newY = baseY + deltaY;
                } else if (handle === 'tr') {
                  newHeight = baseH - 2 * deltaY;
                  newY = baseY + deltaY;
                }
              }
            }
          }

          // Default behavior: corner handles resize proportionally (maintain aspect ratio).
          // Holding Shift makes resizing freeform and will persist the changed
          // dimensions (updateOriginal = true). Side handles always update original dims.
          if (e.shiftKey) {
            // Shift = freeform: allow width/height to change independently
            updateOriginal = true;
          } else {
            // No Shift = maintain aspect ratio for corner handles
            const cornerHandles = ['tl', 'tr', 'br', 'bl'];
            if (cornerHandles.includes(handle)) {
              if (Math.abs(deltaX) > Math.abs(deltaY)) {
                newHeight = newWidth / originalAspectRatio;
                // If resizing from top (or top corners), adjust newY so bottom remains stable
                if (handle === 't' || handle === 'tl' || handle === 'tr') {
                  newY = baseY + (baseH - newHeight);
                }
              } else {
                newWidth = newHeight * originalAspectRatio;
                if (handle === 'l' || handle === 'tl' || handle === 'bl') {
                  newX = baseX + (baseW - newWidth);
                }
              }
            }
            // When maintaining aspect ratio, do NOT persist the changed dimensions
            updateOriginal = false;
          }

          // Only apply if positive dimensions
          if (newWidth > 0 && newHeight > 0) {
            // Update local frame so the visual box changes immediately,
            // then notify parent only when this resize should persist.
            setLocalFrame({ x: Math.round(newX), y: Math.round(newY), width: Math.round(newWidth), height: Math.round(newHeight) });
            if (Math.round(newX) !== Math.round(baseX) || Math.round(newY) !== Math.round(baseY)) {
              onMove(Math.round(newX), Math.round(newY));
            }
            // Side handles should permanently affect the stored/original
            // dimensions so future crops use the new values.
            if (isSideHandle) updateOriginal = true;
            // If this is a proportional corner resize (default), do NOT update the
            // parent dimensions — keep the canonical size unchanged. Only when
            // updateOriginal is true (freeform via Shift or side handle) do we
            // persist the changed dims by calling onResize.
            if (updateOriginal) {
              // Debug: trace when we persist canonical resize during drag
              if (typeof window !== 'undefined' && window && window.console) {
                // eslint-disable-next-line no-console
                console.debug('CropFrame: resizing -> onResize (persist)', { updateOriginal: true, proportional: proportionalResizeRef.current, newWidth: Math.round(newWidth), newHeight: Math.round(newHeight) });
              }
              onResize(Math.round(newWidth), Math.round(newHeight), true, proportionalResizeRef.current);
            } else {
              // If not updateOriginal (proportional corner), skip calling onResize
              // so parent width/height remain unchanged.
              // Non-persisting path removed: do not call onResize for proportional preview
            }
          }
      }
    };

    const handleMouseUp = () => {
      clearTimeout(timeoutId);
      resizeHandleRef.current = null;

      // Read the final localFrame values up-front
  // Prefer the proportional preview ref if this was a proportional resize
  const lfSource = localFrameRef.current || { x, y, width, height };
  const nx = Math.round(lfSource.x);
  const ny = Math.round(lfSource.y);
  const nw = Math.round(lfSource.width);
  const nh = Math.round(lfSource.height);

        if (proportionalResizeRef.current) {
        // For proportional corner resizes: persist visual size but keep label
        // dimensions unchanged (handled in parent via 'visual').
        if (nx !== Math.round(x) || ny !== Math.round(y)) {
          onMove(nx, ny);
        }
        onResize(nw, nh, 'visual');
        setLocalFrame({ x: nx, y: ny, width: nw, height: nh });
      } else {
        // For freeform or side resizes: persist dims and use justPersisted
        // guard to avoid a temporary snap while parent updates.
        if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('CropFrame: mouseup persisting resize', { nx, ny, nw, nh });
        }
        justPersistedRef.current = true;
        if (nx !== Math.round(x) || ny !== Math.round(y)) {
          onMove(nx, ny);
        }
        if (nw !== Math.round(width) || nh !== Math.round(height)) {
          onResize(nw, nh, true, false); // non-proportional persist on mouseup
        }
        setLocalFrame({ x: nx, y: ny, width: nw, height: nh });
      }

      // Finally, clear interaction UI state
      setDragging(false);
      setResizing(false);
      setDraggingStarted(false);
      setGrabbing(false);
      setLabelHover(false);
  proportionalResizeRef.current = false;
    };

    if (dragging || resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, resizing, onMove, onResize, zoomFactor, draggingStarted, timeoutId, originalAspectRatio, x, y, width, height]);

  // Prefer the explicit prop if provided; fall back to legacy global flag for backward compatibility
  const drawingActive = typeof drawMode === 'boolean'
    ? drawMode
    : (typeof window !== 'undefined' && window.__drawingCropFrameActive);
  const handleMouseDown = (e) => {
    // If drawing mode is active, do not allow drag/select/resize on existing frames
    if (drawingActive && !e.shiftKey) {
      e.preventDefault();
      return;
    }
    // Prevent drag/resize if clicking inside the label or edit form
    if (e.target.closest('.frame-label') || e.target.closest('form')) {
      return;
    }
    e.preventDefault();
    // Set a timeout to apply the grabbing class after a delay
    const id = setTimeout(() => {
      if (!resizing) {
        setGrabbing(true);
      }
    }, delay);
    setTimeoutId(id);
  // Capture initial mouse position and current frame metrics in refs so
  // subsequent mousemove calculations are always based on the same baseline.
  // Use the live localFrame if available so interactions always start from
  // the currently-displayed dimensions (fixes jumping back to stale sizes).
  startMouseRef.current = { x: e.clientX, y: e.clientY };
  const baseline = localFrame || { x, y, width, height };
  startFrameRef.current = { x: baseline.x, y: baseline.y, width: baseline.width, height: baseline.height };

  // Any new direct interaction starts from the currently rendered frame

    // Determine click region relative to the frame to decide if this is a
    // resize or drag. We use a small threshold in client pixels.
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const relY = e.clientY - rect.top;
    const threshold = Math.max(8, 12);
    const nearLeft = relX <= threshold;
    const nearRight = relX >= rect.width - threshold;
    const nearTop = relY <= threshold;
    const nearBottom = relY >= rect.height - threshold;

  let clickedHandle = null;
    if (nearLeft && nearTop) clickedHandle = 'tl';
    else if (nearLeft && nearBottom) clickedHandle = 'bl';
    else if (nearRight && nearBottom) clickedHandle = 'br';
    else if (nearRight && nearTop) clickedHandle = 'tr';
    else if (nearTop) clickedHandle = 't';
    else if (nearBottom) clickedHandle = 'b';
    else if (nearLeft) clickedHandle = 'l';
    else if (nearRight) clickedHandle = 'r';

    // top-right is intentionally non-resizable per request
    if (clickedHandle === 'tr') clickedHandle = null;

    const cornerHandles = ['tl', 'tr', 'br', 'bl'];
    // Determine whether this resize should be proportional (default for corners)
    proportionalResizeRef.current = !!(clickedHandle && cornerHandles.includes(clickedHandle) && !e.shiftKey);

    if (clickedHandle) {
      resizeHandleRef.current = clickedHandle;
      setResizing(true);
    } else {
      resizeHandleRef.current = null;
      setDragging(true);
    }
    onSelect(zIndex); // Ensure selection happens only for the specific frame
  };

  // Hover logic removed: no highlighting allowed

  const handleDoubleClick = (e) => {
    e.preventDefault();
    onDoubleClick(zIndex); // Notify parent to handle frame cycling
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onRemove();
  };
  // remove debug logging
  // If drawing is active, disable pointer events on this frame
  return (
    <div
      className={`crop-frame${selected ? ' selected' : ''}${isLargerThanImage ? ' oversize' : ''}${grabbing ? ' grabbing' : ''}${resizing ? ' resizing' : ''}${drawingActive ? ' draw-mode' : ''}${shiftHeld ? ' shift-held' : ''}${isDrawingFrame ? ' current-drawing' : ''}${recentlyDrawn ? ' recently-drawn' : ''}`}
    style={{
  left: `${(usingLocalForRender ? localFrame.x : x)}px`,
  top: `${(usingLocalForRender ? localFrame.y : y)}px`,
  width: `${Math.round(usingLocalForRender ? localFrame.width : width)}px`, // Rounded width for rendering
  height: `${Math.round(usingLocalForRender ? localFrame.height : height)}px`, // Rounded height for rendering
        zIndex,
        borderWidth: `${1 / zoomFactor}px`,
        outlineWidth: `${1 / zoomFactor}px`,
        cursor: drawingActive && !shiftHeld ? 'inherit' : (cursorType || undefined),
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={(e) => {
        // Update cursor based on proximity to edges/corners
        if (drawingActive && !e.shiftKey) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const relX = e.clientX - rect.left;
        const relY = e.clientY - rect.top;
        const threshold = Math.max(8, 12);
        const nearLeft = relX <= threshold;
        const nearRight = relX >= rect.width - threshold;
        const nearTop = relY <= threshold;
        const nearBottom = relY >= rect.height - threshold;
        let hovered = null;
        if (nearLeft && nearTop) hovered = 'nwse-resize';
        else if (nearLeft && nearBottom) hovered = 'nesw-resize';
        else if (nearRight && nearBottom) hovered = 'nwse-resize';
        else if (nearRight && nearTop) hovered = null; // top-right disabled
        else if (nearTop) hovered = 'ns-resize';
        else if (nearBottom) hovered = 'ns-resize';
        else if (nearLeft) hovered = 'ew-resize';
        else if (nearRight) hovered = 'ew-resize';
        else hovered = undefined;
        setCursorType(hovered);
      }}
      // onMouseEnter and onMouseLeave removed: no highlighting
      onDoubleClick={handleDoubleClick} // Add double-click handler
    >
      <div
        className="frame-label"
        style={{ fontSize: `${1.3 / zoomFactor}rem` }}
        onMouseEnter={() => setLabelHover(true)}
        onMouseLeave={() => setLabelHover(false)}
        onMouseDown={e => {
          if (drawingActive && !e.shiftKey) {
            e.preventDefault();
            return;
          }
          if (!editing) {
              // Only allow drag if not editing
              e.stopPropagation(); // Prevent parent drag
              // Initialize refs baseline and start dragging from the label
              startMouseRef.current = { x: e.clientX, y: e.clientY };
              const baseline = localFrame || { x, y, width, height };
              startFrameRef.current = { x: baseline.x, y: baseline.y, width: baseline.width, height: baseline.height };
              setDragging(true);
              onSelect(zIndex);
            }
        }}
      >
        {editing ? (
          <span className="frame-label-dims" style={{ minWidth: `${dimsMinCh}ch` }}>
            <input
              ref={widthInputRef}
              className="frame-edit-input frame-edit-width"
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              value={editWidth}
              style={{ width: `${Math.max(String(editWidth).length, String(displayWidth).length, 1)}ch` }}
              min={1}
              placeholder="Width"
              onChange={e => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setEditWidth(val ? Number(val) : '');
              }}
              onClick={e => e.stopPropagation()}
              onFocus={e => e.target.select()}
              onKeyDown={e => {
                if (e.key === 'Enter' && editWidth > 0 && editHeight > 0) {
                  e.preventDefault();
                  onResize(editWidth, editHeight, true, false);
                  setEditing(false);
                }
              }}
            />
            <span className="frame-edit-px"> x </span>
            <input
              ref={heightInputRef}
              className="frame-edit-input frame-edit-height"
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              value={editHeight}
              style={{ width: `${Math.max(String(editHeight).length, String(displayHeight).length, 1)}ch` }}
              min={1}
              placeholder="Height"
              onChange={e => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setEditHeight(val ? Number(val) : '');
              }}
              onClick={e => e.stopPropagation()}
              onFocus={e => e.target.select()}
              onKeyDown={e => {
                if (e.key === 'Enter' && editWidth > 0 && editHeight > 0) {
                  e.preventDefault();
                  onResize(editWidth, editHeight, true, false);
                  setEditing(false);
                }
              }}
            />
            <span className="frame-edit-px">px ({displayAspectRatio.toFixed(2)})</span>
            <button
              type="button"
              className="edit-dimensions-btn"
              onClick={e => {
                e.stopPropagation();
                if (editWidth > 0 && editHeight > 0) {
                  onResize(editWidth, editHeight, true, false);
                  setEditing(false);
                }
              }}
            >
              Set
            </button>
          </span>
        ) : (
          <span className="frame-label-dims" style={{ minWidth: `${dimsMinCh}ch` }}>
            {displayWidth} x {displayHeight}px ({displayAspectRatio.toFixed(2)})
            {isLargerThanImage && (
              <Tooltip message="Output exceeds image dimensions; upscaling will occur.">
                <span className="frame-alert" aria-label="Output exceeds image dimensions; upscaling will occur.">&#9888;</span>
              </Tooltip>
            )}
            {isLargerThanImage && (
              <Tooltip message="Constrain crop area to image bounds.">
                <button
                  type="button"
                  className="constrain-frame-btn"
                  aria-label="Constrain to image bounds"
                  onClick={e => {
                    e.stopPropagation();
                    onConstrain();
                  }}
                >
                  <span className="constrain-icon" aria-hidden="true">
                    <span>&#8600;</span><span>&#8601;</span><span>&#8599;</span><span>&#8598;</span>
                  </span>
                </button>
              </Tooltip>
            )}
            <Tooltip message="Lock crop framing position and aspect ratio.">
              <button
                type="button"
                className={`lock-frame-btn${isLocked ? ' active' : ''}`}
                aria-label="Lock crop framing"
                aria-pressed={Boolean(isLocked)}
                onClick={e => {
                  e.stopPropagation();
                  onToggleLock();
                }}
              >&#128274;</button>
            </Tooltip>
            <button
              className="edit-dimensions-btn"
              onClick={e => {
                e.stopPropagation();
                setEditWidth(Math.round(initialWidth || width));
                setEditHeight(Math.round(initialHeight || height));
                setEditing(true);
              }}
            >
              Edit
            </button>
          </span>
        )}
      </div>
    {/* No explicit DOM handles — cursor and hit-testing determine resize areas */}
      <button
        className="remove-button"
        onClick={handleRemove}
        style={{
          '--remove-button-font-size': `${18 / zoomFactor}px`,
          '--remove-button-width': `${20 / zoomFactor}px`,
          '--remove-button-height': `${20 / zoomFactor}px`,
          '--remove-button-right': `${-8 / zoomFactor}px`,
          '--remove-button-top': `${-10 / zoomFactor}px`,
        }}
      />
    </div>
  );
}

export default CropFrame;
